/**
 * Cookie / analytics consent - single source of truth.
 *
 * GDPR Art. 7(3) requires "as easy to withdraw as to give". We persist
 * a single string in localStorage and dispatch a `resumark:consent`
 * event whenever it changes so the analytics layer can flip on/off
 * without a reload.
 *
 * Values:
 *   "accepted" - user pressed Accept all
 *   "rejected" - user pressed Reject all (or Customize → save with
 *                 analytics off)
 *   null - never asked (default), behave as rejected for capture
 */
export type ConsentValue = "accepted" | "rejected";

export const CONSENT_KEY = "resumark.consent.v1";
export const CONSENT_EVENT = "resumark:consent";

/** The privacy-notice version in force - stamped onto every recorded consent so
 *  the ledger shows WHICH notice the user agreed to. Bump on material changes
 *  (keep in sync with PrivacyPage "Last updated"). Bumping it ALSO re-prompts
 *  the sign-in Terms/age gate on every device (see isPolicyAccepted). */
export const CONSENT_POLICY_VERSION = "2026-06-02";

// Per-device record that the Terms/age sign-in gate was accepted for a given
// policy version. This is a UX hint ONLY (so the heavy gate shows once per
// device, not every login) - the legal record is the server consent ledger,
// written on every actual sign-in. Versioned so a CONSENT_POLICY_VERSION bump
// auto-re-prompts. Fail-open: any read error / mismatch → treat as NOT accepted
// so the gate re-shows (we never skip the gate on error).
const POLICY_ACCEPT_KEY = "resumark.policy.v1";

/** True only when THIS device affirmatively accepted the CURRENT policy version. */
export function isPolicyAccepted(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(POLICY_ACCEPT_KEY);
    if (!raw) return false;
    const env = JSON.parse(raw) as { version?: string };
    return env.version === CONSENT_POLICY_VERSION;
  } catch {
    return false; // unparseable / blocked → re-gate
  }
}

/** Record this device's acceptance of the current policy version (called at
 *  sign-in success, after the affirmative checkbox gate). */
export function setPolicyAccepted(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      POLICY_ACCEPT_KEY,
      JSON.stringify({ version: CONSENT_POLICY_VERSION, acceptedAt: new Date().toISOString() }),
    );
  } catch {
    /* storage blocked - next visit just re-shows the gate (fail-open) */
  }
}

// Per-purpose consent (beyond the binary analytics cookie) - each purpose is an
// independent, opt-in (default false) choice, stored as a small JSON map.
export type ConsentPurpose = "ai_features";
const PURPOSES_KEY = "resumark.consent.purposes.v1";

function readPurposes(): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(PURPOSES_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

/** Read a per-purpose consent. Defaults to FALSE - opt-in, never pre-granted. */
export function getPurposeConsent(purpose: ConsentPurpose): boolean {
  return readPurposes()[purpose] === true;
}

/** Set a per-purpose consent and notify live subscribers. */
export function setPurposeConsent(purpose: ConsentPurpose, granted: boolean): void {
  if (typeof window === "undefined") return;
  try {
    const map = readPurposes();
    map[purpose] = granted;
    window.localStorage.setItem(PURPOSES_KEY, JSON.stringify(map));
  } catch {
    /* private mode - degrade gracefully */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT, { detail: { purpose, granted } }),
    );
  } catch {
    /* ignore */
  }
}

/** Read the persisted choice. Returns null when the user hasn't answered. */
export function getConsent(): ConsentValue | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (raw === "accepted" || raw === "rejected") return raw;
    return null;
  } catch {
    return null;
  }
}

/** Persist a choice + dispatch the consent event for live subscribers. */
export function setConsent(value: ConsentValue): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* private mode - degrade gracefully */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT, { detail: { value } }),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Forget the choice so the banner re-prompts. Wired to the
 * "Manage cookie preferences" link at the bottom of /privacy.
 */
export function resetConsent(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT, { detail: { value: null } }),
    );
  } catch {
    /* ignore */
  }
}
